// Iter56 — /material-movements (Logistics Requests) workspace page.
//
// Lifecycle: requested → in_progress → completed (+ cancelled).
// Two sections:
//   §1 Open — requested + in_progress
//   §2 History — completed + cancelled
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useAuth } from "../context/AuthContext";
import { hasModuleAccess } from "../lib/moduleAccess";
import { NewMaterialMovementModal } from "../components/NewMaterialMovementModal";
import { UniversalFilter } from "../components/filters/UniversalFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  requested: { bg: "rgba(243,177,74,0.16)", fg: "#f3b14a", label: "REQUESTED" },
  in_progress: { bg: "rgba(155,89,182,0.16)", fg: "#c89adb", label: "IN PROGRESS" },
  completed: { bg: "rgba(78,208,132,0.16)", fg: "#9aebc2", label: "COMPLETED" },
  cancelled: { bg: "rgba(120,125,140,0.16)", fg: "#9aa1b3", label: "CANCELLED" },
};

const DIRECTION_LABEL = {
  to_sorting: { label: "→ Sorting", color: "#9ec3ec" },
  from_sorting: { label: "Sorting →", color: "#9aebc2" },
  between_locations: { label: "↔ Between", color: "#c5cad8" },
};

const PRIORITY_COLOR = { high: "#E24B4A", medium: "#f3b14a", low: "#9aa1b3" };

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

export default function MaterialMovements() {
  const { user } = useAuth();
  const addToast = useStore((s) => s.addToast);

  const canView = hasModuleAccess(user, "material_movement");
  const canCreate = (hasModuleAccess(user, "material_movement") || hasModuleAccess(user, "sorting")) &&
    ["responder", "escalation_manager", "tenant_admin", "platform_admin"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, requested: 0, in_progress: 0, completed: 0, cancelled: 0 });
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [focusId, setFocusId] = useState(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const f = params.get("focus");
      if (f) setFocusId(f);
      if (params.get("new") === "1") setNewOpen(true);
    } catch (_) { /* noop */ }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/material-movements?limit=300`);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (err) {
      addToast({ type: "error", title: "Failed to load logistics requests", body: err.message });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { if (canView) refresh(); }, [refresh, canView]);

  const chips = useMemo(() => [
    { key: "status", label: "Status", type: "select", options: [
      { value: "requested", label: "Requested", predicate: (i) => i.status === "requested" },
      { value: "in_progress", label: "In progress", predicate: (i) => i.status === "in_progress" },
      { value: "completed", label: "Completed", predicate: (i) => i.status === "completed" },
      { value: "cancelled", label: "Cancelled", predicate: (i) => i.status === "cancelled" },
    ]},
    { key: "direction", label: "Direction", type: "select", options: [
      { value: "to_sorting", label: "To sorting", predicate: (i) => i.direction === "to_sorting" },
      { value: "from_sorting", label: "From sorting", predicate: (i) => i.direction === "from_sorting" },
      { value: "between_locations", label: "Between", predicate: (i) => i.direction === "between_locations" },
    ]},
    { key: "priority", label: "Priority", type: "select", options: [
      { value: "high", label: "High", predicate: (i) => i.priority === "high" },
      { value: "medium", label: "Medium", predicate: (i) => (i.priority || "medium") === "medium" },
      { value: "low", label: "Low", predicate: (i) => i.priority === "low" },
    ]},
    { key: "link", label: "Link", type: "select", options: [
      { value: "linked", label: "Linked to incident", predicate: (i) => Boolean(i.incident_id) },
      { value: "standalone", label: "Standalone", predicate: (i) => !i.incident_id },
    ]},
    { key: "date_chip", label: "Requested", type: "dateRange", field: "requested_at" },
  ], []);

  const searchText = useCallback((i) => [
    i.id, i.part_number, i.from_location, i.to_location, i.note,
    i.incident_id, i.incident_title, i.requested_by_name,
  ].filter(Boolean).join(" "), []);

  const open = useMemo(() => items.filter((x) => ["requested", "in_progress"].includes(x.status)), [items]);
  const history = useMemo(() => items.filter((x) => ["completed", "cancelled"].includes(x.status)), [items]);

  const callTransition = async (id, action) => {
    try {
      await axios.patch(`${API}/material-movements/${id}/${action}`, action === "complete" ? {} : {});
      addToast({ type: "success", title: `Logistics request ${action === "start" ? "started" : action === "complete" ? "completed" : "cancelled"}`, body: id });
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      addToast({ type: "error", title: "Action failed", body: typeof msg === "string" ? msg : "Try again" });
    }
  };

  if (!canView) {
    return (
      <div data-testid="material-movements-workspace">
        <div className="rounded-md p-6 text-center" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.08)", ...mono(11, "var(--text-dim, #9198b0)") }} data-testid="material-movements-no-access">
          You don't have access to the Logistics Requests module.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="material-movements-workspace">
      <Header canCreate={canCreate} onNew={() => setNewOpen(true)} />

      {newOpen && (
        <NewMaterialMovementModal
          onClose={() => setNewOpen(false)}
          onCreated={() => { setNewOpen(false); refresh(); }}
        />
      )}

      <Section title="§1 · Open Requests" testId="material-movements-open-section">
        {open.length === 0 ? (
          <Empty text={loading ? "Loading…" : "No open logistics requests."} testId="material-movements-open-empty" />
        ) : (
          <UniversalFilter
            storageKey="material_movements_open"
            items={open}
            getSearchText={searchText}
            chips={chips}
            placeholder="Search id, part #, locations, incident…"
            testIdPrefix="material-movements-uf"
            emptyMessage="No requests match the current filter."
          >
            {({ filtered }) => (
              <Table
                rows={filtered}
                cols={["ID", "Direction", "From → To", "Part / qty", "Status", "Requested", ""]}
                widths={[130, 130, "flex-1", 150, 120, 130, 160]}
                testId="material-movements-open-table"
                rowRenderer={(mv) => (
                  <MoveRow key={mv.id} mv={mv} focus={focusId === mv.id} canAct={canCreate || canView} onAction={callTransition} />
                )}
              />
            )}
          </UniversalFilter>
        )}
      </Section>

      <Section title="§2 · History" testId="material-movements-history-section">
        {history.length === 0 ? (
          <Empty text={loading ? "Loading…" : "No completed or cancelled requests yet."} testId="material-movements-history-empty" />
        ) : (
          <UniversalFilter
            storageKey="material_movements_history"
            items={history}
            getSearchText={searchText}
            chips={chips}
            placeholder="Search id, part #, locations…"
            testIdPrefix="material-movements-history-uf"
            emptyMessage="No history rows match the current filter."
          >
            {({ filtered }) => (
              <Table
                rows={filtered}
                cols={["ID", "Direction", "From → To", "Part / qty", "Status", "Closed", ""]}
                widths={[130, 130, "flex-1", 150, 120, 130, 160]}
                testId="material-movements-history-table"
                rowRenderer={(mv) => (
                  <MoveRow key={mv.id} mv={mv} focus={focusId === mv.id} canAct={false} onAction={callTransition} />
                )}
              />
            )}
          </UniversalFilter>
        )}
      </Section>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="material-movements-stats">
        <StatCard label="Requested" value={counts.requested ?? 0} color="#f3b14a" testId="material-movements-stat-requested" />
        <StatCard label="In progress" value={counts.in_progress ?? 0} color="#c89adb" testId="material-movements-stat-in-progress" />
        <StatCard label="Completed" value={counts.completed ?? 0} color="#9aebc2" testId="material-movements-stat-completed" />
        <StatCard label="Total" value={counts.all ?? 0} color="#9ec3ec" testId="material-movements-stat-total" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

const Header = ({ canCreate, onNew }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
    <div>
      <h1
        style={{
          fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800,
          fontSize: "clamp(26px, 3.4vw, 34px)", color: "var(--text, #f0f2f8)",
          letterSpacing: "-0.5px", lineHeight: 1.05,
        }}
        data-testid="view-title"
      >
        Logistics Requests
      </h1>
      <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
        Move material to/from sorting or between locations
      </div>
    </div>
    {canCreate && (
      <button
        type="button" onClick={onNew}
        className="px-4 py-2 rounded-md"
        style={{
          background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif",
          fontWeight: 700, fontSize: 13, letterSpacing: "0.4px",
          cursor: "pointer", border: "none",
        }}
        data-testid="material-movements-new-btn"
      >
        ＋ New Logistics Request
      </button>
    )}
  </div>
);

const MoveRow = ({ mv, focus, canAct, onAction }) => {
  const status = STATUS_BADGE[mv.status] || STATUS_BADGE.requested;
  const dir = DIRECTION_LABEL[mv.direction] || { label: mv.direction, color: "#9ec3ec" };
  const requested = mv.requested_at ? new Date(mv.requested_at).toLocaleString() : "—";
  const closed = mv.completed_at ? new Date(mv.completed_at).toLocaleString() : "—";
  const pri = mv.priority || "medium";
  const widths = [130, 130, "flex-1", 150, 120, 130, 160];

  const actionLabel = mv.status === "requested" ? "Start →" : mv.status === "in_progress" ? "Complete →" : null;
  const actionVerb = mv.status === "requested" ? "start" : "complete";

  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5"
      style={{
        background: focus ? "rgba(24,95,165,0.10)" : "var(--bg-surface, #0d0f18)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        borderLeft: focus ? "2px solid #185FA5" : "2px solid transparent",
      }}
      data-testid={`material-movements-row-${mv.id}`}
    >
      <div style={{ flex: `0 0 ${widths[0]}px`, minWidth: 0 }}>
        <span style={mono(11, "#9ec3ec")}>{mv.id}</span>
        <div style={mono(9, PRIORITY_COLOR[pri] || "#9aa1b3", undefined, "0.4px")} className="uppercase">{pri}</div>
      </div>
      <div style={{ flex: `0 0 ${widths[1]}px`, minWidth: 0 }}>
        <span style={mono(11, dir.color, 700, "0.5px")}>{dir.label}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "var(--text, #f0f2f8)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {(mv.from_location || "—")} → {(mv.to_location || "—")}
        </div>
        <div style={mono(9, "#9198b0")} className="truncate">
          {mv.incident_id ? `${mv.incident_id} · ${mv.incident_title || ""}` : (mv.sorting_result_id ? `from ${mv.sorting_result_id}` : "Standalone")}
        </div>
      </div>
      <div style={{ flex: `0 0 ${widths[3]}px`, minWidth: 0 }}>
        <div style={mono(11, "#c5cad8")}>{mv.part_number || "—"}</div>
        <div style={mono(9, "#9aebc2")}>{mv.quantity != null ? `${mv.quantity.toLocaleString()} pcs` : ""}</div>
      </div>
      <div style={{ flex: `0 0 ${widths[4]}px` }}>
        <span style={{ background: status.bg, color: status.fg, ...mono(9, undefined, undefined, "0.5px"), padding: "2px 7px", borderRadius: 3 }}>
          {status.label}
        </span>
      </div>
      <div style={{ flex: `0 0 ${widths[5]}px`, minWidth: 0 }}>
        <span style={mono(10, "#c5cad8")}>{mv.status === "completed" || mv.status === "cancelled" ? closed : requested}</span>
        <div style={mono(9, "#9198b0")} className="truncate">
          {mv.status === "completed" ? `by ${mv.completed_by_name || "—"}` : `by ${mv.requested_by_name || "—"}`}
        </div>
      </div>
      <div style={{ flex: `0 0 ${widths[6]}px`, display: "flex", gap: 6, justifyContent: "flex-end" }}>
        {actionLabel && canAct ? (
          <>
            <button
              type="button" onClick={() => onAction(mv.id, actionVerb)}
              className="px-2.5 py-1 rounded"
              style={{
                background: actionVerb === "complete" ? "rgba(78,208,132,0.16)" : "rgba(24,95,165,0.16)",
                border: `1px solid ${actionVerb === "complete" ? "rgba(78,208,132,0.55)" : "rgba(24,95,165,0.55)"}`,
                color: actionVerb === "complete" ? "#9aebc2" : "#9ec3ec",
                ...mono(10, undefined, undefined, "0.4px"),
                cursor: "pointer", whiteSpace: "nowrap",
              }}
              data-testid={`material-movements-action-${actionVerb}-${mv.id}`}
            >
              {actionLabel}
            </button>
            {mv.status === "requested" && (
              <button
                type="button" onClick={() => onAction(mv.id, "cancel")}
                className="px-2 py-1 rounded"
                style={{
                  background: "transparent", border: "1px solid rgba(255,255,255,0.14)",
                  color: "#9aa1b3", ...mono(10, undefined, undefined, "0.4px"),
                  cursor: "pointer", whiteSpace: "nowrap",
                }}
                data-testid={`material-movements-action-cancel-${mv.id}`}
              >
                ✕
              </button>
            )}
          </>
        ) : <span style={mono(9, "#6b7388")}>—</span>}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const Section = ({ title, children, testId }) => (
  <div className="mb-8" data-testid={testId}>
    <h2 className="mb-3" style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)", letterSpacing: "-0.2px" }}>{title}</h2>
    {children}
  </div>
);

const StatCard = ({ label, value, color, testId }) => (
  <div
    className="rounded-md p-3"
    style={{ background: "var(--bg-surface, #111420)", border: "1px solid rgba(255,255,255,0.12)" }}
    data-testid={testId}
  >
    <div style={mono(9, "#9198b0", undefined, "1.2px")} className="uppercase">{label}</div>
    <div className="mt-2" style={{ ...mono(22, color, 500), lineHeight: 1 }}>{value}</div>
  </div>
);

const Empty = ({ text, testId }) => (
  <div
    className="rounded-md p-6 text-center"
    style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.08)", ...mono(11, "var(--text-dim, #9198b0)") }}
    data-testid={testId}
  >
    {text}
  </div>
);

const Table = ({ rows, cols, widths, testId, rowRenderer }) => (
  <div className="rounded-md overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid={testId}>
    <div className="flex items-center gap-3 px-3 py-2" style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      {cols.map((c, i) => (
        <div key={i} style={{
          flex: widths[i] === "flex-1" ? 1 : `0 0 ${widths[i]}px`,
          width: widths[i] === "flex-1" ? undefined : widths[i],
          ...mono(9, "#9198b0", undefined, "1.2px"), textTransform: "uppercase",
        }}>{c}</div>
      ))}
    </div>
    {rows.map(rowRenderer)}
  </div>
);
